import { useState } from "react";
import { useMutation } from "react-query";
import { t } from "@/i18n-js/instance";
import type { CreateSubmissionPayload } from "@/react/api/submissionsApi";
import { submissionsApi } from "@/react/api/submissionsApi";
import { useModalCookie } from "@/react/components/FormRenderer/hooks/useModalCookie";
import type { ApiError } from "@/react/config/CustomErrors";
import type { Form } from "@circle-react/types/Form";
import { useToast } from "@circle-react-uikit/ToastV2";

interface ElementFieldMap {
  [key: number]: number;
}
const elementFieldsToMap = (form: Form): ElementFieldMap => {
  const elementFieldMap: ElementFieldMap = {};
  let elementFieldsIndex = 0;
  form.elements.forEach(element => {
    if (element.type === "field") {
      elementFieldMap[element.elementable_id] = elementFieldsIndex;
      elementFieldsIndex++;
    }
  });
  return elementFieldMap;
};

interface SubmissionResponse {
  success: boolean;
}
interface UseSubmitFormProps {
  form: Form;
  index: number;
  isModal: boolean;
  setErrors: any;
}
export const useSubmitForm = ({
  form,
  index,
  setErrors,
  isModal,
}: UseSubmitFormProps) => {
  const toast = useToast();
  const { suppressPopupForm } = useModalCookie({
    form,
  });
  const [shouldRenderThankYouPage, setShouldRenderThankYouPage] =
    useState(false);
  const elementFieldsMap = elementFieldsToMap(form);

  const { mutate, isLoading: isSubmitting } = useMutation<
    SubmissionResponse,
    ApiError,
    CreateSubmissionPayload
  >({
    mutationFn: payload => submissionsApi.create({ formId: form.id, payload }),
    onSuccess: () => {
      if (isModal) {
        suppressPopupForm();
      }
      if (form.after_submission_action === "thank_you_page") {
        setShouldRenderThankYouPage(true);
      } else if (form.after_submission_action === "redirect") {
        // eslint-disable-next-line sonarjs/post-message -- Disabled for plugin version update. Fix when possible.
        window.parent.postMessage(
          {
            type: "redirect",
            formUid: form.uid,
            formIndex: index,
            url: form.redirect_url,
          },
          "*",
        );
      }
    },
    onError: error => {
      if (
        error.errorDetails &&
        Array.isArray(error.errorDetails) &&
        error.errorDetails.length > 0
      ) {
        const errors: Record<number, string> = error.errorDetails.reduce(
          (acc, errorDetail) => {
            acc[elementFieldsMap[errorDetail.elementable_id]] =
              errorDetail.error;
            return acc;
          },
          {},
        );
        setErrors(errors);
      } else if (error.status === 429) {
        toast.error(t("settings.forms.errors.too_many_submissions"));
      } else {
        toast.error(error.message);
      }
    },
  });

  return { mutate, isSubmitting, shouldRenderThankYouPage };
};
